import React, { useState, useEffect, useRef } from 'react';
import { Plus, CreditCard, LogOut, Loader2, Image as ImageIcon, AlertCircle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { LanguageSelector } from '../components/common';
import { DeleteAccountModal } from '../components/modals';
import NativeBridge, { isNativeApp, canPurchaseDigitalGoods } from '../native-bridge';

// Helper to extract filename from image URL (fallback when filename field is missing)
const extractFilenameFromUrl = (url) => {
  if (!url || typeof url !== 'string' || url.startsWith('data:')) return null;
  try {
    const urlObj = new URL(url);
    const segments = urlObj.pathname.split('/').filter(Boolean);
    const filename = segments[segments.length - 1];
    // Validate it looks like a UUID-based filename (20-50 chars with valid extension)
    if (/^[a-zA-Z0-9\-_]{20,50}\.(jpg|jpeg|png|webp|gif)$/i.test(filename)) {
      return filename;
    }
  } catch (e) {
    // URL parsing failed
  }
  return null;
};

const Dashboard = ({ boards, setBoards, onCreateNew, onEditBoard, onLogout, onNavigate }) => {
  const { token, userStats, handleBuyCredits, deleteAccount } = useAuth();
  const { t } = useTranslation();
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const refreshingImagesRef = useRef(new Set());
  const failedImagesRef = useRef(new Set()); // Track images that failed to load to prevent infinite retries

  useEffect(() => {
    if (token) {
      loadBoards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadBoards = async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const data = await api.getBoards(token);
      const boardsMap = {};
      data.boards.forEach(board => {
        boardsMap[board.id] = {
          ...board,
          parsedData: JSON.parse(board.data || '{}')
        };
      });
      setBoards(boardsMap);

      // Proactively refresh any expired image URLs (awaited to ensure completion)
      await refreshExpiredUrls(boardsMap);

      // Update iOS widget with images from all boards for rotation
      if (isNativeApp()) {
        const boardsArray = Object.entries(boardsMap);
        const allImageUrls = [];
        let firstBoardId = null;
        let firstBoardTitle = null;

        // Collect images from all boards (up to 8 for rotation)
        for (const [boardId, board] of boardsArray) {
          const canvasImages = board.parsedData?.canvasImages || [];
          for (const img of canvasImages) {
            if (img.imageUrl && allImageUrls.length < 8) {
              allImageUrls.push(img.imageUrl);
              if (!firstBoardId) {
                firstBoardId = boardId;
                firstBoardTitle = board.title;
              }
            }
          }
          if (allImageUrls.length >= 8) break;
        }

        if (allImageUrls.length > 0) {
          NativeBridge.updateWidget({
            imageUrl: allImageUrls[0],  // Legacy single image
            imageUrls: allImageUrls,    // Multiple images for rotation
            title: firstBoardTitle || t('dashboard.defaultBoardTitle'),
            affirmation: t('dashboard.defaultAffirmation'),
            boardId: firstBoardId
          });
        }
      }
    } catch (e) {
      console.error('Error loading boards:', e);
      setLoadError(true);
    }
    setIsLoading(false);
  };

  const refreshImageUrl = async (boardId, image) => {
    // Try to get filename from image object, or extract from URL as fallback
    let filename = image?.filename;
    if (!filename && image?.imageUrl) {
      filename = extractFilenameFromUrl(image.imageUrl);
      if (filename) {
        console.log(`[Dashboard] Extracted filename from URL: ${filename}`);
      }
    }

    if (!filename || !token) {
      console.warn(`[Dashboard] Cannot refresh image - no filename available`, { imageId: image?.id, hasUrl: !!image?.imageUrl });
      if (image?.id) {
        setFailedImages(prev => new Set(prev).add(`${boardId}:${image.id}`));
      }
      return;
    }

    const refreshKey = `${boardId}:${image.id}`;
    if (refreshingImagesRef.current.has(refreshKey)) {
      return;
    }

    refreshingImagesRef.current.add(refreshKey);
    try {
      const data = await api.getImageUrl(token, filename);
      if (data?.url) {
        // Clear from failed images since we got a new URL
        failedImagesRef.current.delete(refreshKey);
        setFailedImages(prev => {
          if (!prev.has(refreshKey)) return prev;
          const next = new Set(prev);
          next.delete(refreshKey);
          return next;
        });
        setBoards(prev => {
          const board = prev[boardId];
          if (!board) return prev;
          const parsedData = board.parsedData || {};
          const canvasImages = (parsedData.canvasImages || []).map(img =>
            img.id === image.id
              ? { ...img, imageUrl: data.url, expiresAt: data.expiresAt, filename: filename }
              : img
          );
          return {
            ...prev,
            [boardId]: {
              ...board,
              parsedData: { ...parsedData, canvasImages },
            }
          };
        });
      }
    } catch (error) {
      console.error('Failed to refresh image URL:', error);
      setFailedImages(prev => new Set(prev).add(refreshKey));
    } finally {
      refreshingImagesRef.current.delete(refreshKey);
    }
  };

  // Proactively refresh expired image URLs
  const refreshExpiredUrls = async (boardsMap) => {
    const now = Date.now();
    const REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes buffer before expiration

    for (const [boardId, board] of Object.entries(boardsMap)) {
      const canvasImages = board.parsedData?.canvasImages || [];

      // Find images that need refresh:
      // - Has filename OR can extract filename from URL
      // - AND (no expiresAt OR expiresAt is expired/expiring soon)
      const needsRefresh = canvasImages.filter(img => {
        // Check if we can get a filename (from field or URL)
        const hasFilename = img.filename || extractFilenameFromUrl(img.imageUrl);
        if (!hasFilename) return false;

        // No expiresAt means unknown expiration - refresh to be safe
        if (!img.expiresAt) return true;

        // Expired or expiring within threshold
        return img.expiresAt < now + REFRESH_THRESHOLD;
      });

      if (needsRefresh.length > 0) {
        console.log(`[Dashboard] Refreshing ${needsRefresh.length} expired/unknown image URLs for board ${boardId}`);
        // Refresh images in parallel
        await Promise.all(needsRefresh.map(img => refreshImageUrl(boardId, img)));
      }
    }
  };

  // Assume canvas is 800px wide for percentage calculations
  const canvasWidth = 800;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <img
              src="logo.png"
              alt={t('common.appName')}
              className="w-48 sm:w-[17rem] mb-1 sm:mb-2"
            />
            <h1 className="sr-only">{t('dashboard.pageTitle')}</h1>
            <p className="text-sm sm:text-base text-gray-600">{t('dashboard.manageBoards')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <LanguageSelector />
            <button
              onClick={onLogout}
              aria-label={t('auth.logout')}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-[44px] text-gray-700 hover:bg-white rounded-lg transition"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">{t('auth.logout')}</span>
            </button>
            {/* In the iOS app, deletion lives in the native Settings screen so
                that the keychain and web storage are cleared together. */}
            {!isNativeApp() && (
              <button
                onClick={() => setShowDeleteAccount(true)}
                aria-label={t('account.deleteTitle')}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-[44px] text-gray-500 hover:text-red-600 hover:bg-white rounded-lg transition"
              >
                <Trash2 className="w-5 h-5" />
                <span className="hidden sm:inline">{t('account.deleteTitle')}</span>
              </button>
            )}
            <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border-2 border-purple-100 w-full sm:w-auto sm:order-first">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">{t('dashboard.imageCredits')}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">{userStats.remaining}</span>
                    <span className="text-sm text-gray-500">/ {userStats.quota}</span>
                  </div>
                </div>
                {/* Credits are digital content: on iOS they would have to be
                    sold through In-App Purchase (guideline 3.1.1), so the app
                    shows the balance but offers no way to buy more. */}
                {canPurchaseDigitalGoods() && (
                  <button
                    onClick={handleBuyCredits}
                    aria-label={t('payment.buyMore')}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-[44px] bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition text-sm font-medium"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>{t('payment.buyMore')}</span>
                  </button>
                )}
              </div>
              {canPurchaseDigitalGoods() && (
                <p className="text-xs text-gray-400 mt-2 hidden sm:block">{t('payment.priceInfo')}</p>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center gap-4 py-20">
            <AlertCircle className="w-10 h-10 text-red-500" />
            <p className="text-gray-700 text-center max-w-sm">{t('dashboard.loadFailed')}</p>
            <button
              onClick={loadBoards}
              className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <button
              onClick={onCreateNew}
              className="aspect-square bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-dashed border-purple-300 flex flex-col items-center justify-center gap-4 group hover:border-purple-500"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition">
                <Plus className="w-8 h-8 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-700 group-hover:text-purple-600">{t('dashboard.createNewBoard')}</span>
            </button>

            {Object.entries(boards).map(([boardId, board]) => {
              const canvasImages = board.parsedData.canvasImages || [];
              const canvasElements = board.parsedData.canvasElements || [];
              const background = board.parsedData.background || { type: 'gradient', value: 'from-gray-50 to-gray-100' };
              const canvasSize = board.parsedData.canvasSize || { width: 210, height: 297 };

              return (
                <button
                  key={boardId}
                  onClick={() => onEditBoard(boardId)}
                  className="aspect-square bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden group"
                >
                  <div className="relative w-full h-full">
                    {canvasImages.length > 0 || canvasElements.length > 0 ? (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        {/* Miniature canvas preview */}
                        <div
                          className="relative"
                          style={{
                            width: '100%',
                            aspectRatio: `${canvasSize.width} / ${canvasSize.height}`,
                            maxWidth: '100%',
                            maxHeight: '100%',
                          }}
                        >
                          {/* Background */}
                          <div
                            className={`absolute inset-0 ${
                              background.type === 'gradient' ? `bg-gradient-to-br ${background.value}` :
                              background.type === 'solid' ? background.value :
                              background.type === 'pattern' && background.value === 'dots' ? 'bg-white' :
                              background.type === 'pattern' && background.value === 'grid' ? 'bg-white' :
                              'bg-gray-100'
                            }`}
                            style={
                              background.type === 'pattern' && background.value === 'dots' ? {
                                backgroundImage: 'radial-gradient(circle, #ddd 1px, transparent 1px)',
                                backgroundSize: '20px 20px'
                              } :
                              background.type === 'pattern' && background.value === 'grid' ? {
                                backgroundImage: 'linear-gradient(#ddd 1px, transparent 1px), linear-gradient(90deg, #ddd 1px, transparent 1px)',
                                backgroundSize: '20px 20px'
                              } : {}
                            }
                          />

                          {/* Canvas Images - scaled down */}
                          {canvasImages.map((img) => {
                            const failKey = `${boardId}:${img.id}`;
                            const placementStyle = {
                              left: `${(img.x / canvasWidth) * 100}%`,
                              top: `${(img.y / canvasWidth) * 100}%`,
                              width: `${(img.width / canvasWidth) * 100}%`,
                              height: `${(img.height / canvasWidth) * 100}%`,
                              transform: `rotate(${img.rotation}deg)`,
                              zIndex: img.zIndex,
                            };
                            if (failedImages.has(failKey)) {
                              return (
                                <div
                                  key={img.id}
                                  className="absolute rounded bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 flex items-center justify-center"
                                  style={placementStyle}
                                >
                                  <ImageIcon className="w-1/3 h-1/3 text-gray-400" aria-hidden="true" />
                                </div>
                              );
                            }
                            return (
                            <img
                              key={img.id}
                              src={img.imageUrl}
                              alt=""
                              className="absolute"
                              onLoad={() => {
                                failedImagesRef.current.delete(failKey);
                              }}
                              onError={() => {
                                // Refresh the signed URL once; after that show a
                                // styled placeholder instead of a broken glyph
                                if (!failedImagesRef.current.has(failKey)) {
                                  failedImagesRef.current.add(failKey);
                                  console.log(`[Dashboard] Image load failed, attempting refresh: ${img.id}`);
                                  refreshImageUrl(boardId, img);
                                } else {
                                  setFailedImages(prev => {
                                    const next = new Set(prev);
                                    next.add(failKey);
                                    return next;
                                  });
                                }
                              }}
                              style={{ ...placementStyle, objectFit: 'cover' }}
                            />
                            );
                          })}

                          {/* Canvas Elements (emojis, text) - scaled down */}
                          {canvasElements.map((el) => (
                            <div
                              key={el.id}
                              className="absolute flex items-center justify-center"
                              style={{
                                left: `${(el.x / canvasWidth) * 100}%`,
                                top: `${(el.y / canvasWidth) * 100}%`,
                                width: `${(el.width / canvasWidth) * 100}%`,
                                height: `${(el.height / canvasWidth) * 100}%`,
                                transform: `rotate(${el.rotation}deg)`,
                                zIndex: el.zIndex,
                                fontSize: el.type === 'emoji' ? `${(el.width / canvasWidth) * 100 * 0.6}%` : `${(el.fontSize / canvasWidth) * 100}%`,
                              }}
                            >
                              {el.type === 'emoji' ? (
                                <span>{el.content}</span>
                              ) : (
                                <p style={{
                                  color: el.color,
                                  fontWeight: el.fontWeight,
                                  textAlign: el.textAlign,
                                  fontSize: `${(el.fontSize / canvasWidth) * 400}px`,
                                  margin: 0,
                                  lineHeight: 1,
                                }}>
                                  {el.content}
                                </p>
                              )}
                            </div>
                          ))}

                          {/* Frame */}
                          {board.parsedData.canvasFrame && (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                border: board.parsedData.canvasFrame.border,
                                boxShadow: board.parsedData.canvasFrame.shadow,
                                borderBottom: board.parsedData.canvasFrame.bottomBorder ? `${board.parsedData.canvasFrame.bottomBorder} solid #fff` : board.parsedData.canvasFrame.border,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-purple-300" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <p className="text-white font-semibold text-sm">
                        {board.title || t('dashboard.untitled')}
                      </p>
                      <p className="text-white/80 text-xs">
                        {new Date(board.sealed_at || board.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <DeleteAccountModal
        isOpen={showDeleteAccount}
        onClose={() => setShowDeleteAccount(false)}
        onConfirm={deleteAccount}
      />
    </div>
  );
};

export default Dashboard;
